import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect, ErpTable, Modal, toOptions } from "../../components/erp";
import { errorText } from "../../api/client";
import { freezingApi, FreezingRow, prodDropdownsApi, productionTracksApi } from "./api";

const today = () => new Date().toISOString().slice(0, 10);

const NewFreezingModal: React.FC<{ productionDate: string; editRow?: FreezingRow; onClose: () => void; onSaved: () => void }> = ({ productionDate, editRow, onClose, onSaved }) => {
  const dd = useQuery({ queryKey: ["prod", "dropdowns"], queryFn: prodDropdownsApi.get, staleTime: 5 * 60_000 });
  const plants = useMemo(() => toOptions(dd.data?.Data?.Plant, (p) => p.ProcessingStationID, (p) => p.StationName), [dd.data]);
  const varieties = useMemo(() => toOptions(dd.data?.Data?.Variety, (v) => v.OptID, (v) => v.Name), [dd.data]);
  const grades = dd.data?.Data?.ProductionGrades || []; const freezingTypes = dd.data?.Data?.FreezingType || [];
  const packingTypes = dd.data?.Data?.PackingType || []; const soakingTypes = dd.data?.Data?.SoakingType || [];
  const [trackId, setTrackId] = useState(String(editRow?.TrackID ?? ""));
  const [plantId, setPlantId] = useState(String(editRow?.PlantID ?? ""));
  const tracks = useQuery({ queryKey: ["prod", "tracks", productionDate, plantId], enabled: !!plantId,
    queryFn: () => productionTracksApi.list({ ArrivalDate: productionDate, PlantID: plantId, Type: "Soaking" }) });
  const tubs = useQuery({ queryKey: ["prod", "tub-numbers", trackId], enabled: !!trackId, queryFn: () => freezingApi.tubNumbers(trackId) });
  const [v, setV] = useState({
    TubNumber: editRow?.TubNo ?? "", Variety: editRow?.VarietyID ?? "", Grade: editRow?.Grade ?? "", PackingType: editRow?.PackingType ?? "",
    FreezingType: editRow?.FreezingType ?? "", Soaking: "", ProductionWeight: String(editRow?.ProductionWeight ?? ""),
    LineNumber: editRow?.LineNumber ?? "", Remarks: editRow?.Remarks ?? "",
  });
  const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      const r = await freezingApi.post({ ProductionID: editRow?.ProductionID ?? "", ProductionDate: productionDate, PlantID: plantId, TrackID: trackId, ...v });
      if (r.Error) setErr(r.Error); else onSaved();
    } catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={editRow ? "Edit Freezing" : "New Freezing Entry"} onClose={onClose} testId="freezing-modal">
      <form onSubmit={submit} data-testid="freezing-form">
        <div className="row">
          <div className="col-md-4 mb-3"><label className="form-label">Plant</label>
            <ErpSelect id="freezing-plant" value={plantId} onChange={setPlantId} options={plants} testId="freezing-plant" /></div>
          <div className="col-md-4 mb-3"><label className="form-label">Track</label>
            <ErpSelect id="freezing-track" value={trackId} onChange={setTrackId}
              options={(tracks.data?.Data || []).map((t) => ({ value: String(t.TrackID), label: t.TrackCode }))} testId="freezing-track" /></div>
          <div className="col-md-4 mb-3"><label className="form-label">Tub Number</label>
            <ErpSelect id="freezing-tub" value={String(v.TubNumber)} onChange={(val) => setV((s) => ({ ...s, TubNumber: val }))}
              options={(tubs.data?.Data || []).map((t) => ({ value: t.TubNumber, label: `${t.TubNumber} (${t.SoakInQty}kg soaked)` }))} testId="freezing-tub" /></div>
        </div>
        <div className="row">
          <div className="col-md-3 mb-3"><label className="form-label">Variety</label>
            <ErpSelect id="freezing-variety" value={String(v.Variety)} onChange={(val) => setV((s) => ({ ...s, Variety: val }))} options={varieties} testId="freezing-variety" /></div>
          <div className="col-md-3 mb-3"><label className="form-label">Grade</label>
            <select className="form-select" value={v.Grade} onChange={(e) => setV((s) => ({ ...s, Grade: e.target.value }))} data-testid="freezing-grade">
              <option value="">Select</option>{grades.map((g) => <option key={g.Grade} value={g.Grade}>{g.Grade}</option>)}
            </select></div>
          <div className="col-md-3 mb-3"><label className="form-label">Freezing Type</label>
            <select className="form-select" value={v.FreezingType} onChange={(e) => setV((s) => ({ ...s, FreezingType: e.target.value }))} data-testid="freezing-type">
              <option value="">Select</option>{freezingTypes.map((o) => <option key={o.OptID} value={o.Value}>{o.Name}</option>)}
            </select></div>
          <div className="col-md-3 mb-3"><label className="form-label">Packing Type</label>
            <select className="form-select" value={v.PackingType} onChange={(e) => setV((s) => ({ ...s, PackingType: e.target.value }))} data-testid="freezing-packing-type">
              <option value="">Select</option>{packingTypes.map((o) => <option key={o.OptID} value={o.Value}>{o.Name}</option>)}
            </select></div>
        </div>
        <div className="row">
          <div className="col-md-4 mb-3"><label className="form-label">Soaking Type</label>
            <select className="form-select" value={v.Soaking} onChange={(e) => setV((s) => ({ ...s, Soaking: e.target.value }))} data-testid="freezing-soaking-type">
              <option value="">Select</option>{soakingTypes.map((o) => <option key={o.OptID} value={o.Value}>{o.Name}</option>)}
            </select></div>
          <div className="col-md-4 mb-3"><label className="form-label">Production Weight (Kgs)</label>
            <input className="form-control" value={v.ProductionWeight} onChange={(e) => setV((s) => ({ ...s, ProductionWeight: e.target.value }))} data-testid="freezing-production-weight" /></div>
          <div className="col-md-4 mb-3"><label className="form-label">Line Number</label>
            <input className="form-control" value={v.LineNumber} onChange={(e) => setV((s) => ({ ...s, LineNumber: e.target.value }))} data-testid="freezing-line-number" /></div>
        </div>
        <div className="mb-3"><label className="form-label">Remarks</label>
          <input className="form-control" value={v.Remarks} onChange={(e) => setV((s) => ({ ...s, Remarks: e.target.value }))} data-testid="freezing-remarks" /></div>
        {err && <Alert kind="danger" testId="freezing-error">{err}</Alert>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid="freezing-submit">{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of v3/Production/freezing-trackid.php — final Freezing step into ProdFreezingData, filtered
 * by Production date/plant/stock-holder/packing-type (per source's own list filter shape, not
 * strictly per-TrackID). Variety/Grade use Production's own masters directly (the source's Sales-PO
 * cascading dropdown is out of scope for this batch — see MIGRATION.md). */
export default function FreezingPage() {
  const [params, setParams] = useSearchParams();
  const productionDate = params.get("ProductionDate") || today();
  const qc = useQueryClient();

  const detail = useQuery({ queryKey: ["prod", "freezing", productionDate], queryFn: () => freezingApi.get({ ProductionDate: productionDate }) });
  const rows = detail.data?.DailyFreezingList || [];
  const [showNew, setShowNew] = useState(false); const [editRow, setEditRow] = useState<FreezingRow | null>(null);

  const del = async (r: FreezingRow) => {
    if (!window.confirm("Delete this Freezing entry?")) return;
    await freezingApi.delete({ ProductionID: r.ProductionID, ProductionDate: productionDate });
    qc.invalidateQueries({ queryKey: ["prod", "freezing", productionDate] });
  };

  return (
    <>
      <PageHeader title="Freezing" location="Production" />
      <div className="card mb-3"><div className="card-body">
        <div className="row"><div className="col-md-3 mb-3"><label className="form-label">Production Date</label>
          <input className="form-control" type="date" value={productionDate} onChange={(e) => setParams((p) => { p.set("ProductionDate", e.target.value); return p; })} data-testid="freezing-date" /></div></div>
      </div></div>

      <div className="card"><div className="card-body">
        <div className="d-flex justify-content-between mb-3"><h6>Freezing Entries</h6>
          <button className="btn btn-sm btn-success" data-testid="freezing-add-new" onClick={() => setShowNew(true)}>+ New Freezing Entry</button></div>
        <ErpTable<FreezingRow> testId="freezing-table" loading={detail.isLoading} error={detail.isError ? errorText(detail.error) : null}
          rows={rows} columns={[{ key: "TrackCode", title: "Track" }, { key: "ProductionPlantName", title: "Plant" }, { key: "TubNo", title: "Tub" },
            { key: "Grade", title: "Grade" }, { key: "FreezingType", title: "Freezing Type" }, { key: "Kgs", title: "Weight (Kgs)" },
            { key: "Status", title: "Status", render: (r) => r.Status ? "Complete" : "Pending" },
            { key: "Action", title: "Action", render: (r) => (
              <div className="d-flex gap-1">
                <button className="btn btn-sm btn-outline-primary" data-testid={`freezing-edit-${r.ProductionID}`} onClick={() => setEditRow(r)}>Edit</button>
                <button className="btn btn-sm btn-outline-danger" data-testid={`freezing-delete-${r.ProductionID}`} onClick={() => del(r)}>Delete</button>
              </div>
            ) }]} />
      </div></div>
      {(showNew || editRow) && <NewFreezingModal productionDate={productionDate} editRow={editRow || undefined}
        onClose={() => { setShowNew(false); setEditRow(null); }}
        onSaved={() => { setShowNew(false); setEditRow(null); qc.invalidateQueries({ queryKey: ["prod", "freezing", productionDate] }); }} />}
    </>
  );
}
